import React, { Fragment, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { SubscriptionPlanFeaturesData } from './subscription-plan-data';
import { SubscriptionService } from '../../../../core/services';
import { zapPlanFeatures, zapPlanHeading } from '../../../../core/utility';

const subscriptionService: SubscriptionService = new SubscriptionService();

// Currency symbol by ISO-4217. Plans carry `currency` (and `currency_symbol` when
// the API applies geo pricing); default to INR for legacy India-only plans.
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹', USD: '$', CAD: 'CA$', EUR: '€', GBP: '£', AED: 'د.إ', KES: 'KSh', NPR: 'रू',
};
export const currencySymbol = (plan: any): string =>
  plan?.currency_symbol || CURRENCY_SYMBOLS[String(plan?.currency || '').toUpperCase()] || '₹';

interface PricingPlanProps {
  subscription_plan: any;
  makePayment?: (data: any, coupon_code?: string) => void; // Declare makePayment function type
  loading?: boolean; // Loading state
  account_type: string;
  activePlan?: any; // Loading state
  availablePlan?: any;
  influencerId?: number;
  isUSInfluencer?: boolean;
  claimTrial?: () => void;      // 7-day free trial: claim handler
  trialLoading?: boolean;       // 7-day free trial: claim in flight
}

const PricingPlan: React.FC<PricingPlanProps> = ({ availablePlan, subscription_plan, makePayment, loading, account_type, activePlan, influencerId, isUSInfluencer, claimTrial, trialLoading }) => {

  const [planTypeMap] = useState<any>({
    'Monthly': 'Monthly',
    'Annually': 'Annually',
    'Annual Plan': 'Annually',
    'Half-Yearly': 'Half-Yearly',
    'Quarterly': 'Quarterly'
  });

  // ---- 7-day free trial ----
  // The Trial card is an ordinary plan row (amount 0, days 7) carrying
  // eligibility flags stamped on by the plan-listing API, so the card renders
  // from the same data as every other plan — it just prices and buys
  // differently. trial_state_known = 0 means the API had no influencer_id
  // (public pricing page), so we advertise the trial without offering to claim.
  const isTrialPlan = subscription_plan.plan_name === 'Trial';
  const trialStateKnown = Number(subscription_plan.trial_state_known) === 1;
  const trialEligible = Number(subscription_plan.trial_eligible) === 1;
  const trialActive = Number(subscription_plan.trial_active) === 1;
  const trialDaysLeft = Number(subscription_plan.trial_days_left) || 0;
  const trialDays = Number(subscription_plan.days) || 7;

  // ---- Special coupon (BD-issued discount for the influencer yearly plan) ----
  const isInfluencerYearly =
    ['Influencer', 'Talent Partner'].includes(subscription_plan.user_type) &&
    subscription_plan.plan_name !== 'FOC' &&
    ['Annually', 'Annual Plan'].includes(subscription_plan.plan_type);
  // Requires influencerId so the coupon box only renders where it can actually
  // be validated/applied (e.g. the in-app settings subscription page).
  const showCouponInput = !!makePayment && isInfluencerYearly && !isUSInfluencer && !!influencerId;

  const [couponInput, setCouponInput] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponMsg, setCouponMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) { setCouponMsg({ type: 'error', text: 'Please enter a coupon code.' }); return; }
    if (!influencerId) { setCouponMsg({ type: 'error', text: 'Unable to validate coupon right now.' }); return; }
    setApplyingCoupon(true);
    setCouponMsg(null);
    try {
      const res: any = await subscriptionService.validateCoupon({ coupon_code: code, influencer_id: influencerId });
      const row = res?.data?.rows?.[0];
      if (row?.valid) {
        setAppliedCoupon({ ...row, coupon_code: code });
        setCouponMsg({ type: 'success', text: `Coupon applied! You pay ₹${row.special_amount} for the year.` });
      } else {
        setAppliedCoupon(null);
        setCouponMsg({ type: 'error', text: row?.message || res?.info || 'Invalid coupon code.' });
      }
    } catch {
      setAppliedCoupon(null);
      setCouponMsg({ type: 'error', text: 'Could not validate the coupon. Please try again.' });
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponMsg(null);
  };

  const handleBuy = () => {
    if (!makePayment) return;
    if (appliedCoupon) {
      // Charge the special price via the one-time discounted order path.
      makePayment({ ...subscription_plan, amount: appliedCoupon.special_amount }, appliedCoupon.coupon_code);
    } else {
      makePayment(subscription_plan);
    }
  };
  let features;
  if (subscription_plan.plan_name?.toLowerCase() === 'foc') {
    features = ['Influencer', 'Talent Partner'].includes(account_type) ? SubscriptionPlanFeaturesData.influencer.basic_plan_features : SubscriptionPlanFeaturesData.brand.basic_plan_features;
  } else {
    features = ['Influencer', 'Talent Partner'].includes(account_type) ? SubscriptionPlanFeaturesData.influencer.premium_plan_features : SubscriptionPlanFeaturesData.brand.premium_plan_features;
  }
  const isCurrentPlan =
    (subscription_plan.plan_name === 'FOC' && !activePlan) ||
    (activePlan?.plan_name === subscription_plan?.plan_name) ||
    // The coupon plan ('Special Savings') is the yearly plan bought via coupon —
    // show it as the current plan on the normal 'Savings' card.
    (activePlan?.plan_name === 'Special Savings' && subscription_plan?.plan_name === 'Savings');
    
  const sym = currencySymbol(subscription_plan);
  return (
    <Fragment>
      <div className={`pricing-plan ${(subscription_plan.user_type === 'Influencer' || subscription_plan.user_type === 'Talent Partner') ? "pricing-plan-influencer" : ""}`}>
        <div className='pricing-plan-div'>
          <div className='d-flex flex-column  pb-3'>
            <span className="pricing-title">
              <div className="d-flex items-center w-100">
                <span className="me-auto">
                  {subscription_plan.plan_name === 'FOC'
                    ? 'BASIC'
                    : subscription_plan.plan_name}
                </span>

                {isCurrentPlan && (
                  <span
                    className="ps-2 fs-14 ms-auto"
                    style={{ color: '#FFA500' }}
                  >
                    Your Current Plan
                  </span>
                )}
              </div>

              {subscription_plan.plan_name !== 'FOC' &&
                // The trial is never "billed" anything, so it gets its own
                // caption instead of falling through to '(Billed Annually)'.
                !isTrialPlan &&
                subscription_plan.user_type === 'Influencer' && (
                  <span className="ps-2 fs-12 me-auto">
                    {planTypeMap[subscription_plan?.plan_type]
                      ? `(Billed ${planTypeMap[subscription_plan.plan_type]})`
                      : '(Billed Annually)'}
                  </span>
                )}
              {isTrialPlan && (
                <span className="ps-2 fs-12 me-auto">(No payment required)</span>
              )}
            </span>
            {subscription_plan.plan_name === 'FOC' && (
              <span className='d-flex flex-column'>
                <span>
                  <span className="pricing-plan-type pricing-span">Free</span>
                  {/* <span className="pricing-plan-type pricing-span"> ₹0</span> */}
                </span>
                <span className='pricing-description'>By default, this plan is activated until you purchase a premium plan</span>
              </span>
            )}
            {/* Trial: priced as "Free", never as "₹0" (its amount is 0, which
                would otherwise render through the generic price block below). */}
            {isTrialPlan && (
              <span className='d-flex flex-column'>
                <span>
                  <span className="pricing-plan-type pricing-span">Free</span>
                  <span className="ps-2 fs-14">for {trialDays} days</span>
                </span>
                <span className='pricing-description'>
                  {trialActive
                    ? `Your free trial is running — ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} left.`
                    : 'Full access to every premium feature. No card needed — it simply ends after ' + trialDays + ' days.'}
                </span>
              </span>
            )}
            {subscription_plan.plan_name != 'FOC' && !isTrialPlan && subscription_plan.discount === 0 && (
              <span className="pricing-plan-type me-auto"><span className='pricing-span'>{sym}{subscription_plan.subscription_amount}</span></span>
            )
            }
            {subscription_plan.plan_name != 'FOC' && !isTrialPlan && subscription_plan.discount != 0 && (
              <span className='d-flex flex-wrap align-items-center'>
                <span className="pricing-plan-type me-auto">
                  <span style={{ textDecoration: 'line-through', fontWeight:'normal',fontSize:18 }}>
                    {sym}{subscription_plan.subscription_amount}
                  </span>
                  &nbsp;
                  <span>{sym}{subscription_plan.amount}</span>
                  <span className='discount'>
                    SAVE {subscription_plan.discount}%
                  </span>
                </span>
                <span className="brand-pricing-plan-type">
                  <span className='pricing-span'>
                    <span className='d-none' style={{ textDecoration: 'line-through' }}>
                      {sym}{subscription_plan.monthly_subscription_net_amount}
                    </span>
                    <span className='amount-per-month'>{sym}{subscription_plan.monthly_amount}/month</span>
                  </span>
                </span>
              </span>
            )}
            {
              (subscription_plan.description && (subscription_plan.user_type === 'Brand' || subscription_plan.user_type === 'Agency')) && (
                <span className='pricing-description'>{subscription_plan.description}</span>
              )
            }
            {subscription_plan.gst_included === 'true' &&
              (
                <span className="pricing-plan-gst">(inclusive of all taxes)</span>
              )
            }
          </div>
          {/* Trial CTA. Sits ahead of the paid-plan button block, which is
              already inert here because it requires amount !== 0. */}
          {isTrialPlan && trialStateKnown && (
            trialActive ? (
              <button type='button' className='btn btn-primary buy-btn plan-btn' disabled>
                {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left
              </button>
            ) : trialEligible && claimTrial ? (
              <LoadingButton
                className='btn btn-primary buy-btn plan-btn'
                loading={!!trialLoading}
                loadingPosition='start'
                variant='contained'
                onClick={claimTrial}
              >
                Start Free Trial
              </LoadingButton>
            ) : null
          )}
          {makePayment && subscription_plan.amount !== 0 && (['Brand', 'Agency'].includes(subscription_plan.user_type) || (subscription_plan.user_type === 'Influencer' && (subscription_plan.plan_name !== activePlan?.plan_name && subscription_plan.plan_name !== "FOC" && !['Savings', 'Special Savings'].includes(activePlan?.plan_name)))) &&
            (
              <Fragment>
                {/* Special coupon input — influencer yearly plan, non-US users only */}
                {showCouponInput && (
                  <div className='coupon-box mb-2'>
                    {!appliedCoupon ? (
                      <div className='d-flex align-items-center' style={{ gap: 8 }}>
                        <input
                          type='text'
                          className='form-control form-control-sm'
                          placeholder='Have a coupon code?'
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value); setCouponMsg(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }}
                          disabled={applyingCoupon}
                          style={{ textTransform: 'none', maxWidth: 200 }}
                        />
                        <button
                          type='button'
                          className='btn btn-outline-primary btn-sm'
                          onClick={applyCoupon}
                          disabled={applyingCoupon || !couponInput.trim()}
                        >
                          {applyingCoupon ? 'Applying…' : 'Apply'}
                        </button>
                      </div>
                    ) : (
                      <div className='d-flex align-items-center' style={{ gap: 8 }}>
                        <span className='badge bg-success' style={{ fontSize: 12 }}>
                          {appliedCoupon.coupon_code} applied
                        </span>
                        <button type='button' className='btn btn-link btn-sm p-0' onClick={removeCoupon}>
                          Remove
                        </button>
                      </div>
                    )}
                    {couponMsg && (
                      <div
                        className={`fs-12 mt-1 ${couponMsg.type === 'success' ? 'text-success' : 'text-danger'}`}
                      >
                        {couponMsg.text}
                      </div>
                    )}
                    {appliedCoupon && (
                      <div className='fs-14 mt-1'>
                        <span style={{ textDecoration: 'line-through', color: '#888' }}>
                          ₹{appliedCoupon.original_amount}
                        </span>
                        &nbsp;
                        <strong>₹{appliedCoupon.special_amount}</strong>
                        <span className='discount ms-1'>/ year</span>
                      </div>
                    )}
                  </div>
                )}
                <LoadingButton
                  className='btn btn-primary buy-btn plan-btn'
                  loading={loading}
                  loadingPosition='start'
                  variant='contained'
                  onClick={handleBuy}
                  disabled={subscription_plan.user_type === 'Influencer' && (['Savings', 'Special Savings'].includes(activePlan?.plan_name) || (activePlan?.plan_name === 'Starter' && subscription_plan?.plan_name === 'Starter'))}
                >
                  {activePlan !== null ? ((subscription_plan.user_type === 'Influencer' || subscription_plan.user_type === 'Talent Partner') ? 'Subscribe' : 'Upgrade') : 'Buy Now'}
                </LoadingButton>
              </Fragment>
            )
          }
          {/* {makePayment &&
          subscription_plan.amount !== 0 &&
          activePlan?.plan_name !== subscription_plan?.plan_name && (
            <LoadingButton
              className="btn btn-primary buy-btn plan-btn"
              loading={loading}
              loadingPosition="start"
              variant="contained"
              onClick={() => makePayment(subscription_plan)}
            >
              {activePlan ? "Upgrade" : "Buy Now"}
            </LoadingButton>
        )} */}
        </div>
        <div className='pricing-plan-detail-sec'>
          {(subscription_plan.user_type === 'Influencer' || subscription_plan.user_type === 'Talent Partner') && (
            <>
              <section className='zap-section'>
                {/* OLD: ten <li> blocks written inline, each reading an
                    entitlement and labelling it in the data model's vocabulary
                    ("rule per automation", "trigger keyword per rule", "zap
                    links per rule", "Ask to Follow & button messages") — the
                    exact words the simplified Zap wizard stopped using — and
                    with no mention of Collect leads at all. The list now comes
                    from zapPlanFeatures(), shared with mobile and the public
                    pricing site so the three cannot drift. */}
                <strong>{zapPlanHeading(subscription_plan.plan_name)}</strong>
                <ul>
                  {zapPlanFeatures(subscription_plan.entitlements, subscription_plan.plan_name).map((feature: string) => (
                    <li key={feature}>
                      <i className='bi bi-check'></i>
                      {feature}
                    </li>
                  ))}

                  {/* <li>
          <i className='bi bi-check'></i>
          {subscription_plan.entitlements?.auto_dm_branding_required
            ? "ClanConnect branding included"
            : "No ClanConnect branding"}
        </li> */}
                </ul>
              </section>
              {/* Foreign / Zap-only users (zap_only stamped by the API) only get
                  the Zap feature, so hide the non-Zap marketplace sections
                  (Campaigns / Creative Suite / Analytics / Invoicing). */}
              {!subscription_plan.zap_only && (
              <ul>
                {/* {features.map((feature, index) => (
                <li key={index} className={feature.liClassName}>
                  {feature.iconClassName && <i className={feature.iconClassName} />}
                  <span>{feature.text}</span>
                </li>
              ))} */}
                {features.map((feature, index) => {

                  const isBasic = subscription_plan.plan_name === 'FOC';

                  // For BASIC → only first item (Live Briefs) should be check
                  let iconClass = feature.iconClassName;

                  if (isBasic) {
                    if (index === 1) {
                      iconClass = 'bi bi-check';
                    } else if (feature.iconClassName) {
                      iconClass = 'bi bi-x';
                    }
                  }

                  return (
                    <li key={index} className={feature.liClassName}>
                      {iconClass && <i className={iconClass} />}
                      <span>{feature.text}</span>
                    </li>
                  );
                })}
              </ul>
              )}
            </>
          )}
          {(subscription_plan.user_type === 'Brand' || subscription_plan.user_type === 'Agency') && !subscription_plan.zap_only && (
            <ul>
              {features.map((feature, index) => (
                <li key={index} className={feature.liClassName}>
                  <span>{feature.text}</span>
                  {feature.iconClassName && <i className={feature.iconClassName} />}
                  {feature.value && <span>{feature.value}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* {subscription_plan.user_type === 'Influencer' &&
      <p className="fs-14 font-italic pb-5"><strong>Please note:</strong> ClanConnect is an influencer marketplace where we bring influencers and brands on a common plane in the most transparent manner. The selection of influencers for campaigns is the sole prerogative of the brand and ClanConnect has no role in this. Also, it is advisable to have a minimum of 1000 followers/subscribers to apply for a campaign.</p>
      } */}
    </Fragment>
  );
};

export default PricingPlan;
